import { Badge, badgeVariants } from "components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ComponentProps, KeyboardEvent, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { SearchIcon, X } from "lucide-react";
import { isDefined, isEmpty } from "helper/utils";

import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "@/assets";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export interface MultiInputWithSuggestionsProps extends Omit<
  ComponentProps<"input">,
  "onChange" | "onKeyDown" | "value" | "size"
> {
  selectedValues: string[];
  onChangeValues: (values: string[]) => void;
  badgeVariant?: BadgeVariant;
  validateBeforeAdd?: (value: string) => boolean;
  suggestions?: string[] | undefined;
  onInputValueChange: (value: string) => void;
  label?: string;
}

/**
 * Select with tags, transform input to tag (scroll the menu).
 * User can use: tab, enter, comma to add an item.
 * **/
export const MultiSelectSuggestions = ({
  badgeVariant = "default",
  onChangeValues,
  validateBeforeAdd,
  className,
  suggestions,
  onInputValueChange,
  selectedValues,
  ...inputProps
}: MultiInputWithSuggestionsProps) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  // select an item
  const selectItem = (value: string) => {
    onChangeValues([...selectedValues, value]);
  };

  // Remove a single value
  const removeValue = (value: string) => {
    const newValues = selectedValues.filter((val) => val !== value);
    onChangeValues(newValues);
  };

  // Clear all selected values
  const clearValues = () => {
    onChangeValues([]);
  };

  const addItem = (value: string) => {
    const val = value.trim();
    if (val && (!validateBeforeAdd || validateBeforeAdd(val))) {
      onChangeValues(selectedValues.concat(value));
      setInputValue("");
      onInputValueChange("");
    }
  };
  //press the "," character to add new item
  const handleInputChange = (value: string | undefined) => {
    if (isDefined(value)) {
      if (!/^,+$/.test(value) && value.endsWith(",")) {
        addItem(value.slice(0, -1));
      } else {
        setInputValue(value);
        onInputValueChange(value);
      }
    }
  };
  //press the tab, enter character to add new item
  const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (isEmpty(inputValue)) {
      return;
    }
    if (["Tab", "Enter"].includes(ev.key)) {
      ev.preventDefault();
      addItem(inputValue);
    }
  };

  return (
    <div className="flex flex-col">
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 overflow-x-auto max-w-full mb-1">
          {selectedValues.map((value, idx) => {
            return (
              <Badge
                key={`selectedItem-${idx}`}
                variant={badgeVariant}
                className="max-w-full break-words whitespace-normal"
              >
                {value}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      removeValue(value);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => removeValue(value)}
                  type="button"
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button variant="outline" aria-expanded={open} className={cn("w-full justify-between", className)}>
            {inputProps.placeholder}
            <ChevronDownIcon
              className={`${open && "rotate-180 transform"} transition duration-150 ease-in-out w-5`}
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <div className="flex h-9 items-center gap-2 border-b px-3">
              <SearchIcon className="size-4 shrink-0 opacity-50" />
              <input
                className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={inputProps.placeholder}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                value={inputValue}
              />
            </div>
            <CommandEmpty>No record found.</CommandEmpty>
            <CommandGroup className="max-h-[120px] overflow-y-auto">
              {suggestions?.map((item, idx) => (
                <CommandItem
                  key={`suggestion-${item}-${idx}`}
                  onSelect={() => selectItem(item)}
                  className="cursor-pointer"
                >
                  <span>{item}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {selectedValues.length > 0 && (
              <div className="border-t p-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    clearValues();
                    setOpen(false);
                  }}
                >
                  Clear selections
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
